import { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { useEvent } from '../../contexts/EventContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Calendar, Plus, Edit, Trash2, Star, Heart, Download, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

export default function EventsTab({ 
  currentUser,
  events, 
  classes,
  categories,
  votingCategories,
  fetchEvents,
  fetchClasses,
  fetchCategories,
  fetchVotingCategories
}) {
  const { currentEvent, setCurrentEvent } = useEvent();
  
  // Event modal state
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'upcoming'
  });
  
  // Class modal state
  const [showClassModal, setShowClassModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classForm, setClassForm] = useState({ name: '', description: '' });
  
  // Judging Category modal state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  
  // Voting Category modal state
  const [showVotingCategoryModal, setShowVotingCategoryModal] = useState(false);
  const [selectedVotingCategory, setSelectedVotingCategory] = useState(null);
  const [votingCategoryForm, setVotingCategoryForm] = useState({ name: '', description: '' });

  // Drag-and-drop reorder state for the Classes section. Tracks the id of
  // the row currently being dragged so we can show a subtle highlight on
  // the row beneath the cursor. Pure HTML5 DnD — no extra library.
  const [draggedClassId, setDraggedClassId] = useState(null);
  const [dragOverClassId, setDragOverClassId] = useState(null);

  const persistClassOrder = async (orderedIds) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/classes/reorder?event_id=${currentEvent.id}`,
        { ordered_ids: orderedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchClasses();
    } catch (err) {
      console.error('Reorder failed', err);
      toast.error(err.response?.data?.detail || 'Failed to save new order');
      // Best-effort revert by re-fetching the server's canonical order.
      fetchClasses();
    }
  };

  const moveClass = (classId, direction) => {
    const current = (classes || []).slice();
    const idx = current.findIndex((c) => c.id === classId);
    if (idx < 0) return;
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= current.length) return;
    [current[idx], current[target]] = [current[target], current[idx]];
    persistClassOrder(current.map((c) => c.id));
  };

  const onClassDragStart = (e, id) => {
    setDraggedClassId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch (_e) {}
  };
  const onClassDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== dragOverClassId) setDragOverClassId(id);
  };
  const onClassDrop = (e, targetId) => {
    e.preventDefault();
    const sourceId = draggedClassId;
    setDraggedClassId(null);
    setDragOverClassId(null);
    if (!sourceId || sourceId === targetId) return;
    const current = (classes || []).slice();
    const sourceIdx = current.findIndex((c) => c.id === sourceId);
    const targetIdx = current.findIndex((c) => c.id === targetId);
    if (sourceIdx < 0 || targetIdx < 0) return;
    const [moved] = current.splice(sourceIdx, 1);
    current.splice(targetIdx, 0, moved);
    persistClassOrder(current.map((c) => c.id));
  };
  const onClassDragEnd = () => {
    setDraggedClassId(null);
    setDragOverClassId(null);
  };
  
  const [submitting, setSubmitting] = useState(false);

  // Helper to format date for input (handles timezone)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Use UTC to avoid timezone shifts
    return date.toISOString().split('T')[0];
  };

  // Helper to create date without timezone conversion
  const createDateFromInput = (dateString) => {
    if (!dateString) return null;
    // Append T12:00:00 to avoid timezone edge cases
    return new Date(dateString + 'T12:00:00').toISOString();
  };

  // ============= EVENT MANAGEMENT =============
  
  const openCreateEventModal = () => {
    setSelectedEvent(null);
    setEventForm({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      status: 'active'
    });
    setShowEventModal(true);
  };

  const openEditEventModal = (event) => {
    setSelectedEvent(event);
    setEventForm({
      name: event.name,
      description: event.description || '',
      start_date: formatDateForInput(event.start_date),
      end_date: formatDateForInput(event.end_date),
      status: event.status || 'active'
    });
    setShowEventModal(true);
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/events`, {
        name: eventForm.name,
        description: eventForm.description,
        start_date: createDateFromInput(eventForm.start_date),
        end_date: createDateFromInput(eventForm.end_date)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Event created successfully');
      setShowEventModal(false);
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        name: eventForm.name,
        description: eventForm.description,
        status: eventForm.status
      };
      
      if (eventForm.start_date) {
        updateData.start_date = createDateFromInput(eventForm.start_date);
      }
      if (eventForm.end_date) {
        updateData.end_date = createDateFromInput(eventForm.end_date);
      }
      
      await axios.put(`${API}/events/${selectedEvent.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Event updated successfully');
      setShowEventModal(false);
      setSelectedEvent(null);
      fetchEvents();
      
      if (currentEvent?.id === selectedEvent.id) {
        const updatedEvents = await axios.get(`${API}/events`);
        const updated = updatedEvents.data.find(e => e.id === selectedEvent.id);
        if (updated) setCurrentEvent(updated);
      }
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This will also delete all associated cars, classes, categories, scores, and configurations.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Event deleted successfully');
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  // ============= CLASS MANAGEMENT =============
  
  const openCreateClassModal = () => {
    setSelectedClass(null);
    setClassForm({ name: '', description: '' });
    setShowClassModal(true);
  };

  const openEditClassModal = (cls) => {
    setSelectedClass(cls);
    setClassForm({ name: cls.name, description: cls.description || '' });
    setShowClassModal(true);
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/classes?event_id=${currentEvent.id}`, classForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Class created successfully');
      setShowClassModal(false);
      fetchClasses();
    } catch (error) {
      console.error('Error creating class:', error);
      toast.error(error.response?.data?.detail || 'Failed to create class');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateClass = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/classes/${selectedClass.id}`, classForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Class updated successfully');
      setShowClassModal(false);
      setSelectedClass(null);
      fetchClasses();
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error(error.response?.data?.detail || 'Failed to update class');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/classes/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Class deleted successfully');
      fetchClasses();
    } catch (error) {
      console.error('Error deleting class:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete class');
    }
  };

  // ============= JUDGING CATEGORY MANAGEMENT =============
  
  const openCreateCategoryModal = () => {
    setSelectedCategory(null);
    setCategoryForm({ name: '', description: '' });
    setShowCategoryModal(true);
  };

  const openEditCategoryModal = (category) => {
    setSelectedCategory(category);
    setCategoryForm({ name: category.name, description: category.description || '' });
    setShowCategoryModal(true);
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/categories?event_id=${currentEvent.id}`, categoryForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Category created successfully');
      setShowCategoryModal(false);
      setCategoryForm({ name: '', description: '' });
      fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error(error.response?.data?.detail || 'Failed to create category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/categories/${selectedCategory.id}`, categoryForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Category updated successfully');
      setShowCategoryModal(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error(error.response?.data?.detail || 'Failed to update category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? All associated scores will also be deleted.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  // ============= VOTING CATEGORY MANAGEMENT =============
  
  const openCreateVotingCategoryModal = () => {
    setSelectedVotingCategory(null);
    setVotingCategoryForm({ name: '', description: '' });
    setShowVotingCategoryModal(true);
  };

  const handleCreateVotingCategory = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/voting-categories?event_id=${currentEvent.id}`, votingCategoryForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Voting category created successfully');
      setShowVotingCategoryModal(false);
      setVotingCategoryForm({ name: '', description: '' });
      fetchVotingCategories();
    } catch (error) {
      console.error('Error creating voting category:', error);
      toast.error(error.response?.data?.detail || 'Failed to create voting category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVotingCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this voting category? All associated votes will also be deleted.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/voting-categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Voting category deleted successfully');
      fetchVotingCategories();
    } catch (error) {
      console.error('Error deleting voting category:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete voting category');
    }
  };

  const handleExportVoterEmails = async () => {
    if (!currentEvent) {
      toast.error('No event selected');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/votes/export?event_id=${currentEvent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      
      if (data.voters.length === 0) {
        toast.info('No voter data to export yet');
        return;
      }
      
      // Create CSV content
      const csvHeaders = ['Email', 'First Voted', 'Total Votes', 'Marketing Consent'];
      const csvRows = data.voters.map(voter => [
        voter.email,
        voter.first_voted ? new Date(voter.first_voted).toLocaleString() : '',
        voter.total_votes,
        voter.marketing_consent ? 'Yes' : 'No'
      ]);
      
      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `voter-emails-${currentEvent.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.total_unique_voters} voter emails (${data.consented_voters} with marketing consent)`);
    } catch (error) {
      console.error('Error exporting voter emails:', error);
      toast.error(error.response?.data?.detail || 'Failed to export voter emails');
    }
  };

  // Helper to format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      timeZone: 'UTC'
    });
  };

  return (
    <div className="space-y-8">
      {/* Events Management - Super Admin Only */}
      {currentUser?.role === 'super_admin' && (
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Events Management</h2>
              <p className="text-gray-600 text-sm">Create and manage car show events</p>
            </div>
            <Button onClick={openCreateEventModal} className="bg-pink-600 hover:bg-pink-700" data-testid="create-event-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          </div>

          <div className="space-y-4">
            {events.map(event => (
              <div 
                key={event.id} 
                className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                  currentEvent?.id === event.id 
                    ? 'border-pink-500 bg-pink-50' 
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-3 h-3 rounded-full ${
                    event.status === 'active' ? 'bg-green-500' :
                    event.status === 'upcoming' ? 'bg-blue-500' :
                    'bg-gray-400'
                  }`} />
                  <div>
                    <h4 className="text-gray-900 font-medium">{event.name}</h4>
                    <p className="text-gray-600 text-sm">
                      {formatDateForDisplay(event.start_date)}
                      {event.status && ` • ${event.status.charAt(0).toUpperCase() + event.status.slice(1)}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {currentEvent?.id === event.id ? (
                    <span className="text-pink-600 text-sm font-medium mr-2">Current</span>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentEvent(event)}
                    >
                      Select
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditEventModal(event)}
                    data-testid={`edit-event-${event.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteEvent(event.id)}
                    data-testid={`delete-event-${event.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Car Classes */}
      <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Car Classes</h2>
            <p className="text-gray-600 text-sm">Classes for {currentEvent?.name || 'current event'}</p>
          </div>
          <Button onClick={openCreateClassModal} className="bg-pink-600 hover:bg-pink-700" data-testid="add-class-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Class
          </Button>
        </div>

        {classes.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg">
            <Calendar className="w-12 h-12 mx-auto text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Classes Defined</h3>
            <p className="text-gray-600 text-sm mb-4">
              Add vehicle classes for this event (e.g., Classic Muscle, Modern Performance)
            </p>
            <Button onClick={openCreateClassModal} variant="outline" className="border-pink-500/50">
              <Plus className="w-4 h-4 mr-2" />
              Create First Class
            </Button>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-500 mb-3">
              Drag the grip handle <GripVertical className="inline w-3 h-3 align-middle" /> or use the up/down arrows to set the order classes appear in the manual-registration dropdown. The order is saved automatically.
            </p>
            <div className="space-y-2" data-testid="classes-reorder-list">
              {classes.map((cls, idx) => (
                <div
                  key={cls.id}
                  draggable
                  onDragStart={(e) => onClassDragStart(e, cls.id)}
                  onDragOver={(e) => onClassDragOver(e, cls.id)}
                  onDrop={(e) => onClassDrop(e, cls.id)}
                  onDragEnd={onClassDragEnd}
                  className={`flex items-center justify-between p-3 border rounded-lg bg-white hover:bg-gray-50 transition-colors ${dragOverClassId === cls.id && draggedClassId !== cls.id ? 'border-pink-400 ring-2 ring-pink-100' : 'border-gray-200'} ${draggedClassId === cls.id ? 'opacity-50' : ''}`}
                  data-testid={`class-row-${cls.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600" title="Drag to reorder">
                      <GripVertical className="w-5 h-5" />
                    </span>
                    <span className="text-xs text-gray-500 font-mono w-8 text-right">#{idx + 1}</span>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-gray-900 font-medium truncate" title={cls.name}>{cls.name}</h4>
                      {cls.description && (
                        <p className="text-gray-600 text-sm truncate">{cls.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => moveClass(cls.id, 'up')}
                      disabled={idx === 0}
                      title="Move up"
                      className="h-8 w-8 p-0"
                      data-testid={`class-move-up-${cls.id}`}
                    >
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => moveClass(cls.id, 'down')}
                      disabled={idx === classes.length - 1}
                      title="Move down"
                      className="h-8 w-8 p-0"
                      data-testid={`class-move-down-${cls.id}`}
                    >
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditClassModal(cls)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteClass(cls.id)}
                      className="h-8 w-8 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Judging Categories */}
      <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Star className="w-6 h-6 text-yellow-500" />
              <h2 className="text-2xl font-bold text-gray-900">Judging Categories</h2>
            </div>
            <p className="text-gray-600 text-sm">Criteria judges will use to score cars (0-10 per category)</p>
          </div>
          <Button onClick={openCreateCategoryModal} className="bg-pink-600 hover:bg-pink-700" data-testid="add-category-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </div>

        {categories.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg">
            <Star className="w-12 h-12 mx-auto text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Judging Categories</h3>
            <p className="text-gray-600 text-sm mb-4">
              Add categories for judges to score cars against (e.g., Exterior, Interior, Engine Bay)
            </p>
            <Button onClick={openCreateCategoryModal} variant="outline" className="border-pink-500/50">
              <Plus className="w-4 h-4 mr-2" />
              Create First Category
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {categories.map((category, index) => (
              <div 
                key={category.id} 
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid={`category-row-${index}`}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="text-gray-900 font-medium">{category.name}</h4>
                    {category.description && (
                      <p className="text-gray-600 text-sm">{category.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditCategoryModal(category)}
                    data-testid={`edit-category-${index}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteCategory(category.id)}
                    data-testid={`delete-category-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-600 text-sm">
                <strong>Total Categories:</strong> {categories.length} • 
                <strong> Max Score Per Car:</strong> {categories.length * 10} points
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Voting Categories (People's Choice) */}
      <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Heart className="w-6 h-6 text-pink-500" />
              <h2 className="text-2xl font-bold text-gray-900">People's Choice Voting</h2>
            </div>
            <p className="text-gray-600 text-sm">Categories for public voting (e.g., Best in Show, Best Paint)</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={handleExportVoterEmails} 
              variant="outline" 
              className="border-green-500 text-green-600 hover:bg-green-50"
              data-testid="export-voters-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Voter Emails
            </Button>
            <Button onClick={openCreateVotingCategoryModal} className="bg-pink-600 hover:bg-pink-700" data-testid="add-voting-category-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Voting Category
            </Button>
          </div>
        </div>

        {votingCategories.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg">
            <Heart className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Voting Categories</h3>
            <p className="text-gray-600 text-sm mb-4">
              Add categories for public voting. Attendees can vote via QR code or the Vote page.
            </p>
            <Button onClick={openCreateVotingCategoryModal} variant="outline" className="border-pink-500/50">
              <Plus className="w-4 h-4 mr-2" />
              Create First Voting Category
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {votingCategories.map((category, index) => (
              <div 
                key={category.id} 
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid={`voting-category-row-${index}`}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center">
                    <Heart className="w-4 h-4 text-pink-500" />
                  </div>
                  <div>
                    <h4 className="text-gray-900 font-medium">{category.name}</h4>
                    {category.description && (
                      <p className="text-gray-600 text-sm">{category.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteVotingCategory(category.id)}
                    data-testid={`delete-voting-category-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            <div className="mt-4 p-3 bg-pink-50 border border-pink-200 rounded-lg">
              <p className="text-pink-700 text-sm">
                <strong>Voting URL:</strong> {window.location.origin}/vote
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Event Create/Edit Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-lg bg-white text-gray-900 border-gray-300">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-900 font-bold">
              {selectedEvent ? 'Edit Event' : 'Create New Event'}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {selectedEvent ? 'Update event details' : 'Create a new car show event'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={selectedEvent ? handleUpdateEvent : handleCreateEvent} className="space-y-4">
            <div>
              <Label>Event Name *</Label>
              <Input
                value={eventForm.name}
                onChange={(e) => setEventForm({...eventForm, name: e.target.value})}
                required
                placeholder="e.g., 2026 Fall Ford Fest"
                className="bg-white border-gray-300 text-gray-900"
                data-testid="event-name-input"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={eventForm.description}
                onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                placeholder="Brief description of the event"
                className="bg-white border-gray-300 text-gray-900"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={eventForm.start_date}
                  onChange={(e) => setEventForm({...eventForm, start_date: e.target.value})}
                  required
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={eventForm.end_date}
                  onChange={(e) => setEventForm({...eventForm, end_date: e.target.value})}
                  required
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>
            </div>

            {selectedEvent && (
              <div>
                <Label>Status</Label>
                <Select
                  value={eventForm.status}
                  onValueChange={(value) => setEventForm({...eventForm, status: value})}
                >
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEventModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-pink-600 hover:bg-pink-700" data-testid="event-submit-btn">
                {submitting ? 'Saving...' : selectedEvent ? 'Update Event' : 'Create Event'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Class Create/Edit Modal */}
      <Dialog open={showClassModal} onOpenChange={setShowClassModal}>
        <DialogContent className="max-w-md bg-white text-gray-900 border-gray-300">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-900 font-bold">
              {selectedClass ? 'Edit Car Class' : 'Add Car Class'}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {selectedClass ? 'Update the class details' : 'Create a new car class for this event'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={selectedClass ? handleUpdateClass : handleCreateClass} className="space-y-4">
            <div>
              <Label>Class Name *</Label>
              <Input
                value={classForm.name}
                onChange={(e) => setClassForm({...classForm, name: e.target.value})}
                required
                placeholder="e.g., Classic Muscle, Modern Performance"
                className="bg-white border-gray-300 text-gray-900"
                data-testid="class-name-input"
              />
            </div>
            
            <div>
              <Label>Description (Optional)</Label>
              <Input
                value={classForm.description}
                onChange={(e) => setClassForm({...classForm, description: e.target.value})}
                placeholder="Brief description of vehicles in this class"
                className="bg-white border-gray-300 text-gray-900"
              />
            </div>

            <DialogFooter className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowClassModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-pink-600 hover:bg-pink-700" data-testid="class-submit-btn">
                {submitting ? 'Saving...' : selectedClass ? 'Update Class' : 'Add Class'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Judging Category Create/Edit Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="max-w-md bg-white text-gray-900 border-gray-300">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-900 font-bold">
              {selectedCategory ? 'Edit Category' : 'Add Judging Category'}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {selectedCategory ? 'Update the category details' : 'Create a new scoring category for judges'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={selectedCategory ? handleUpdateCategory : handleCreateCategory} className="space-y-4">
            <div>
              <Label>Category Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                required
                placeholder="e.g., Exterior, Interior, Engine Bay"
                className="bg-white border-gray-300 text-gray-900"
                data-testid="category-name-input"
              />
            </div>
            
            <div>
              <Label>Description (Optional)</Label>
              <Input
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                placeholder="Brief description of what judges should evaluate"
                className="bg-white border-gray-300 text-gray-900"
              />
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-600 text-sm">
                Judges will score this category from 0-10 points.
              </p>
            </div>

            <DialogFooter className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCategoryModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-pink-600 hover:bg-pink-700" data-testid="category-submit-btn">
                {submitting ? 'Saving...' : selectedCategory ? 'Update Category' : 'Add Category'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Voting Category Create Modal */}
      <Dialog open={showVotingCategoryModal} onOpenChange={setShowVotingCategoryModal}>
        <DialogContent className="max-w-md bg-white text-gray-900 border-gray-300">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-900 font-bold">
              Add Voting Category
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Create a new category for public voting
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateVotingCategory} className="space-y-4">
            <div>
              <Label>Category Name *</Label>
              <Input
                value={votingCategoryForm.name}
                onChange={(e) => setVotingCategoryForm({...votingCategoryForm, name: e.target.value})}
                required
                placeholder="e.g., Best in Show, Best Paint, Best Engine"
                className="bg-white border-gray-300 text-gray-900"
                data-testid="voting-category-name-input"
              />
            </div>
            
            <div>
              <Label>Description (Optional)</Label>
              <Input
                value={votingCategoryForm.description}
                onChange={(e) => setVotingCategoryForm({...votingCategoryForm, description: e.target.value})}
                placeholder="Brief description of what this award is for"
                className="bg-white border-gray-300 text-gray-900"
              />
            </div>

            <div className="p-3 bg-pink-50 border border-pink-200 rounded-lg">
              <p className="text-pink-700 text-sm">
                Attendees can vote once per email per category.
              </p>
            </div>

            <DialogFooter className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowVotingCategoryModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-pink-600 hover:bg-pink-700" data-testid="voting-category-submit-btn">
                {submitting ? 'Saving...' : 'Add Category'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
